import { Alert, Cell, Grid, Heading, HFlow, InfoLabel, VFlow } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { Box } from 'components/Box'
import { DateTime } from 'components/date'
import { NumeroDeclaracaoObito } from 'components/label'
import { LoadingIndicator } from 'components/loading'
import { useFlags } from 'config/useFlagsContext'
import { useCidadaoVisualizacaoQuery } from 'graphql/hooks.generated'
import { Endereco, EnderecoIndigena, LembreteProfissionalIdentificadorEnum, SexoEnum } from 'graphql/types.generated'
import { useLembreteProfissionalComValidade } from 'hooks/lembrete-profissional/useLembreteProfissionalComValidade'
import { useState } from 'react'
import { tipoEnderecoIndigena } from 'view/cadastro-imovel/model-cadastroImovel'
import { CardNovidadesCidadao } from 'view/cidadao/components/CardNovidadesCidadao'

import { CompartilhaProntuarioSection } from './CompartilhaProntuarioSection'
import { CondicoesSaudeAutorreferidasSection } from './CondicoesSaudeAutorreferidasSection'
import { ContatosSection } from './ContatosSection'
import { DadosPessoaisSection } from './DadosPessoaisSection'
import { EnderecoIndigenaSection } from './EnderecoIndigenaSection'
import { EnderecoSection } from './EnderecoSection'
import { InformacoesSociodemograficasSection } from './InformacoesSociodemograficasSection'
import { InformacoesSocioeconomicasSection } from './InformacoesSocioeconomicasSection'
import { PeriodoAusenciaSaudeIndigenaSection } from './PeriodoAusenciaSaudeIndigenaSection'
import { RaniSaudeIndigenaSection } from './RaniSaudeIndigenaSection'
import { ResponsabilidadeAcompanhamentoSection } from './ResponsabilidadeAcompanhamentoSection'
import { SituacaoDeRuaSection } from './SituacaoDeRuaSection'

interface CidadaoInformacoesViewProps {
  cidadaoId: ID
  mostrarCompartilhaProntuario?: boolean
}

export function CidadaoInformacoesView(props: Readonly<CidadaoInformacoesViewProps>) {
  const { cidadaoId, mostrarCompartilhaProntuario = true } = props
  const [showAlertaRecusaCadastro, setShowAlertaRecusaCadastro] = useState(true)
  const { PILOTO_ATENDIMENTO_SOCIOEDUCATIVO_ENABLED } = useFlags()

  const { data: sessionData } = useSession({ fetchPolicy: 'cache-only' })
  const usuarioId = sessionData?.profissional?.usuario?.id

  const { isAtivo: isCardNovidadesAtivo } = useLembreteProfissionalComValidade({
    usuarioId,
    identificador: LembreteProfissionalIdentificadorEnum.CARD_LEMBRETE_PROFISSIONAL_CIDADAO,
    diasValidade: 30,
  })

  const {
    data: { cidadao },
    loading,
  } = useCidadaoVisualizacaoQuery({
    variables: {
      id: cidadaoId,
    },
    fetchPolicy: 'network-only',
  })

  if (loading) return <LoadingIndicator />

  const isEnderecoIndigena = tipoEnderecoIndigena.includes(cidadao?.tipoEndereco)
  const isCadastroForaAps =
    !!cidadao?.cidadaoNucleoFamiliar?.unidadeSaude &&
    cidadao?.cidadaoNucleoFamiliar?.unidadeSaude?.tipo?.isTipoAps !== true
  return (
    <VFlow vSpacing={2}>
      {isCardNovidadesAtivo && <CardNovidadesCidadao />}
      {cidadao?.dataUltimaRecusa && showAlertaRecusaCadastro && (
        <Alert type='warning' onCloseClick={() => setShowAlertaRecusaCadastro(false)} style={{ padding: '0.5rem' }}>
          <b>
            Recusa de atualização do cadastro em <DateTime format='DD/MM/YYYY' value={cidadao.dataUltimaRecusa} />.
          </b>
          A recusa do cidadão à atualização do seu cadastro não implicará no não atendimento deste na unidade de saúde,
          bem como quaisquer outras formas de discriminação. Quando oportuno, tente sensibilizá-lo novamente para que o
          cadastro possa ser atualizado.
        </Alert>
      )}
      {cidadao?.dataObito && (
        <Grid>
          <Cell size={5}>
            <Box>
              <HFlow justifyContent='space-between' alignItems='center'>
                <Heading color='normal' level={3}>
                  Cidadão em óbito
                </Heading>

                <InfoLabel title='Data de óbito'>
                  <DateTime format='DD/MM/YYYY' value={cidadao?.dataObito} />
                </InfoLabel>

                <InfoLabel title='Número da Declaração de Óbito'>
                  <NumeroDeclaracaoObito value={cidadao?.numeroDocumentoObito} />
                </InfoLabel>
              </HFlow>
            </Box>
          </Cell>
          <Cell size={7} />
        </Grid>
      )}

      <DadosPessoaisSection cidadao={cidadao} />
      <ContatosSection
        telefoneResidencial={cidadao.telefoneResidencial}
        telefoneCelular={cidadao.telefoneCelular}
        telefoneContato={cidadao.telefoneContato}
        email={cidadao.email}
      />
      <ResponsabilidadeAcompanhamentoSection
        cidadaoNucleoFamiliar={cidadao.cidadaoNucleoFamiliar}
        microarea={cidadao.microArea}
        isCadastroForaAps={isCadastroForaAps}
        isCadIndividual
      />
      {!isEnderecoIndigena ? (
        <EnderecoSection
          statusSituacaoDeRua={cidadao.situacaoDeRua?.stSituacaoDeRua}
          paisResidencia={cidadao.paisExterior?.nome}
          endereco={cidadao.endereco as Endereco}
          localidadeExterior={cidadao.localidadeExterior}
        />
      ) : (
        <EnderecoIndigenaSection
          enderecoIndigena={cidadao.enderecoIndigena as EnderecoIndigena}
          tipoEndereco={cidadao.tipoEndereco}
          numeroFamilia={cidadao.numeroFamilia}
        />
      )}
      <InformacoesSociodemograficasSection
        informacoesSociodemograficas={cidadao.informacoesSociodemograficas}
        escolaridade={cidadao.escolaridade}
        cbo={cidadao.cbo}
        isAtendimentoSocioeducativoEnabled={PILOTO_ATENDIMENTO_SOCIOEDUCATIVO_ENABLED}
      />
      <InformacoesSocioeconomicasSection
        informacoesSocioeconomicas={cidadao.informacoesSocioEconomicas}
        beneficios={cidadao?.cidadaoAldeado?.beneficios}
      />
      <CondicoesSaudeAutorreferidasSection
        condicoesSaudeAutorreferidas={cidadao.condicoesSaudeAutorreferidas}
        renderGestante={cidadao?.sexo !== SexoEnum.MASCULINO}
      />
      {!!cidadao.cidadaoAldeado && (
        <VFlow vSpacing={2}>
          <RaniSaudeIndigenaSection
            unidadeFunai={cidadao?.cidadaoAldeado?.unidadeFunai}
            cadastroUnico={cidadao?.cidadaoAldeado?.cadastroUnico}
            livro={cidadao?.cidadaoAldeado?.livro}
            folha={cidadao?.cidadaoAldeado?.folha}
            dtEmissao={cidadao?.cidadaoAldeado?.dtEmissao}
            ufNascimento={cidadao?.cidadaoAldeado?.ufNascimento}
          />

          <PeriodoAusenciaSaudeIndigenaSection periodoAusenciaList={cidadao.periodoAusenciaList} />
        </VFlow>
      )}

      {!isCadastroForaAps && cidadao.situacaoDeRua && <SituacaoDeRuaSection situacaoDeRua={cidadao.situacaoDeRua} />}
      {mostrarCompartilhaProntuario && (
        <CompartilhaProntuarioSection statusCompartilhaProntuario={cidadao.stCompartilhaProntuario} />
      )}
    </VFlow>
  )
}
